import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '../api/apiClient';
import {
  acceptInvitation as acceptInvitationRequest,
  getSession,
  login as loginRequest,
  logout as logoutRequest,
} from '../api/authApi';
import type {
  AcceptInvitationRequest,
  AuthenticationStatus,
  LoginRequest,
} from '../types/auth';
import type { User, UserRole } from '../types/user';

interface AuthContextValue {
  currentUser: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<User>;
  acceptInvitation: (invitation: AcceptInvitationRequest) => Promise<User>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  hasRole: (roles?: readonly UserRole[]) => boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: AuthProviderProps) {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthenticationStatus>('loading');

  const refreshSession = useCallback(async () => {
    setStatus('loading');

    try {
      const response = await getSession();
      setCurrentUser(response.user);
      setStatus('authenticated');
    } catch (error) {
      setCurrentUser(null);
      setStatus('anonymous');

      if (!(error instanceof ApiError) || error.status !== 401) {
        throw error;
      }
    }
  }, []);

  useEffect(() => {
    refreshSession().catch(() => {
      // A failed bootstrap is handled as an anonymous session; login surfaces API errors.
    });
  }, [refreshSession]);

  const login = useCallback(async (credentials: LoginRequest): Promise<User> => {
    const response = await loginRequest(credentials);
    setCurrentUser(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const acceptInvitation = useCallback(async (
    invitation: AcceptInvitationRequest,
  ): Promise<User> => {
    const response = await acceptInvitationRequest(invitation);
    setCurrentUser(response.user);
    setStatus('authenticated');
    return response.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutRequest();
    } finally {
      setCurrentUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo(() => {
    const hasRole = (roles?: readonly UserRole[]) => {
      if (!roles || roles.length === 0) {
        return true;
      }

      return Boolean(currentUser && roles.includes(currentUser.role));
    };

    return {
      currentUser,
      isAuthenticated: status === 'authenticated',
      isLoading: status === 'loading',
      login,
      acceptInvitation,
      logout,
      hasRole,
      refreshSession,
    };
  }, [acceptInvitation, currentUser, login, logout, refreshSession, status]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }

  return context;
}
